import type { DiscoverInteractionType } from "@/lib/analytics/discoverInteractions";
import type { FilterRegistryKey } from "./config";

/** The filter names the tracking spec uses: `<name>_filter`. */
export type SpecFilterName =
  | "date"
  | "radius"
  | "sort"
  | "age"
  | "skill"
  | "price"
  | "membership"
  | "club_type";

/**
 * Registry key to spec name. The interaction types (`<name>_filter`) and the
 * remembered listing filters take their names from this table;
 * `listingFilters.ts` lists the keys each tab reports. Events and
 * clubs keep their own sort, age and skill keys. The club membership fee is the
 * clubs' free/paid, so it is price. Membership and club type are only
 * interaction types: the listing context does not report them.
 */
export const SPEC_FILTER_NAME_BY_KEY = {
  date: "date",
  radius: "radius",
  eventSort: "sort",
  clubSort: "sort",
  eventAgeGroup: "age",
  ageGroup: "age",
  eventSkillLevel: "skill",
  skillLevel: "skill",
  price: "price",
  membershipFee: "price",
  membership: "membership",
  clubType: "club_type",
} as const satisfies Record<FilterRegistryKey, SpecFilterName>;

type FilterInteractionType = Extract<
  DiscoverInteractionType,
  `${SpecFilterName}_filter`
>;

/**
 * The interaction type a filter reports, by registry key. `filterKey` is a
 * plain string (`FilterConfig.key`), so `undefined` covers a key that is not
 * in the registry.
 */
export function getFilterInteractionType(
  filterKey: string,
): FilterInteractionType | undefined {
  const name = (
    SPEC_FILTER_NAME_BY_KEY as Readonly<Record<string, SpecFilterName>>
  )[filterKey];
  return name ? (`${name}_filter` as const) : undefined;
}
